import { useAuth } from "@clerk/clerk-react";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { BentoCategory, OrganizationBulkSendSettings } from "schemas/dashboard";

import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";

interface VaSettingsContextInterface {
  fetchLoading: boolean;
  setFetchLoading: Dispatch<SetStateAction<boolean>>;
  fallbackTemplates: Template[];
  setFallbackTemplates: Dispatch<SetStateAction<Template[]>>;
  bulkSendSetting: OrganizationBulkSendSettings;
  setBulkSendSetting: Dispatch<
    SetStateAction<OrganizationBulkSendSettings | null>
  >;
  categoriesToPickFrom: BentoCategory[];
  loadData: () => void;
  fetchSettings: () => void;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  fetchLoading: false,
  setFetchLoading: defaultContextMissingFunction,
  fallbackTemplates: [],
  setFallbackTemplates: defaultContextMissingFunction,
  bulkSendSetting: {} as OrganizationBulkSendSettings,
  setBulkSendSetting: defaultContextMissingFunction,
  categoriesToPickFrom: [],
  loadData: defaultContextMissingFunction,
  fetchSettings: defaultContextMissingFunction,
};

const VaSettingsContext =
  createContext<VaSettingsContextInterface>(defaultInterface);

interface VaSettingsProviderProps {
  children: React.ReactNode;
}

const VASettingsProvider = ({ children }: VaSettingsProviderProps) => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  // States
  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [bulkSendSetting, setBulkSendSetting] =
    useState<OrganizationBulkSendSettings | null>(null);
  const [fallbackTemplates, setFallbackTemplates] = useState<Template[]>([]);
  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);

  const getCategoriesToPickFrom = async () => {
    if (!currentOrg?.isVaUser) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding?for_va=${true}`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const fetchSettings = async () => {
    if (!currentOrg?.isVaUser) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bulk-send-settings`,
        "GET",
      );
      if (res.bulkSendSetting) {
        setBulkSendSetting(res.bulkSendSetting);
        setFallbackTemplates(res.bulkSendSetting?.templates || []);
      }
      return res.templates;
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const loadData = async () => {
    if (!currentOrg?.isVaUser) return;
    setFetchLoading(true);
    try {
      await Promise.all([fetchSettings(), getCategoriesToPickFrom()]);
    } finally {
      setFetchLoading(false);
    }
  };

  return (
    <VaSettingsContext.Provider
      value={{
        fetchLoading,
        setFetchLoading,
        fallbackTemplates,
        setFallbackTemplates,
        bulkSendSetting: bulkSendSetting!,
        setBulkSendSetting,
        categoriesToPickFrom,
        loadData,
        fetchSettings,
      }}
    >
      {children}
    </VaSettingsContext.Provider>
  );
};

export { VASettingsProvider, VaSettingsContext };
